/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * What a seat of each plan unlocks, and who is running out of it.
 *
 * Both blocks on the plan screen read the same two facts — the allowance a
 * plan gives one person, and how much of it the person closest to the
 * ceiling has spent — so they are worked out once, here.
 */

import type React from 'react';
import type { TeamPerson } from '@/services/requests/housien-services/AgenciesService.team';
import type { PlanFeatureItem } from '@/features/pricing';
import {
  enabledFeatures, FEATURE_ROWS,
} from '@/features/onboarding/components/sections/PlanChoice.catalog';

/** Where the upgrade block starts making an offer. */
export const PRESSURE_AT = 0.85;

/** One plan, as the "what a seat unlocks" cards draw it. */
export interface SeatUnlock {
  planId: string;
  title: string;
  /** The tier's gem glyph, from the catalogue. */
  icon?: React.FunctionComponent;
  seatsOnTeam: number;
  /** Auxiliary users one seat on this tier brings, zero when it brings none. */
  supportPerSeat: number;
  interactions: number | null;
  searches: number | null;
  /** How many times the entry tier's allowance this is, or null at entry. */
  timesEntry: number | null;
  /** Row ids of the features this plan has, in the catalogue's own order. */
  features: string[];
  /**
   * Whether the catalogue still sells it today.
   *
   * An announced tier -- `coming_soon`, or `sold_out` -- is listed with
   * `active: false` in both catalogues, so Payien refuses to bill it. The
   * card can still describe such a plan, but it must not offer it.
   */
  sellable: boolean;
}

/**
 * The feature rows a plan actually has, for the card's check list.
 *
 * Only the ones it has: the card answers "what does this seat give", and a
 * cross beside something the reader is being asked to pay for reads as a
 * warning rather than a comparison. The comparison is what the three cards
 * side by side already are.
 *
 * Quota and storage rows are left out -- they are numbers, and the card
 * states them above in their own type sizes rather than as ticked lines.
 *
 * The row vocabulary is the plan screen's, so a buyer who read the tiers
 * before subscribing sees the same names afterwards.
 *
 * @param features - The plan's feature items, as `usePlans` builds them.
 * @returns Row ids, in the order the comparison draws them.
 */
export const includedFeatures = (
  features: PlanFeatureItem[] | undefined,
): string[] => {
  const on = enabledFeatures(features);
  return FEATURE_ROWS
    .flatMap((row) => (row.source.from === 'features' ? [{ id: row.id, keys: row.source.keys }] : []))
    .filter((row) => row.keys.every((key) => on.has(key)))
    .map((row) => row.id);
};

/** Somebody close enough to their ceiling to be worth mentioning. */
export interface UnderPressure {
  person: TeamPerson;
  /** Which quota is tight: the one that is furthest along. */
  quota: 'interactions' | 'searches';
  used: number;
  total: number;
  share: number;
}

/**
 * Share of.
 */
const shareOf = (quota: { used: number; total: number | null } | undefined): number => {
  if (!quota || quota.total == null || quota.total <= 0) return 0;
  return quota.used / quota.total;
};

/**
 * The person closest to a ceiling, when anybody is close enough.
 *
 * One person, not a list: the block makes one offer, and an offer about
 * four people at once is an offer about nobody. Ties go to whoever the
 * roster lists first, which is stable between renders.
 *
 * Storage is deliberately not considered. It is the agency's one pool and
 * upgrading a seat does not enlarge it, so an offer made on its account
 * would be selling something the upgrade does not deliver.
 */
export const mostUnderPressure = (
  people: TeamPerson[], atOrAbove: number = PRESSURE_AT,
): UnderPressure | null => {
  const ranked = (people || [])
    .map((person) => {
      const interactions = shareOf(person.interaction);
      const searches = shareOf(person.partnership_search);
      const tight = interactions >= searches ? 'interactions' as const : 'searches' as const;
      const quota = tight === 'interactions' ? person.interaction : person.partnership_search;
      return {
        person, quota: tight, share: Math.max(interactions, searches),
        used: quota?.used ?? 0, total: quota?.total ?? 0,
      };
    })
    .filter((candidate) => candidate.share >= atOrAbove)
    .sort((a, b) => b.share - a.share);
  return ranked[0] ?? null;
};

/**
 * What each plan gives one seat, and how many of them the team has.
 *
 * `timesEntry` is how many times the cheapest plan's interaction allowance
 * this one is — the number the cards lead with, because "800" means nothing
 * to somebody who has not memorised that the entry tier gives 200.
 */
export const seatUnlocks = (
  plans: Array<{
    planId: string; title: string;
    icon?: React.FunctionComponent;
    interactions: number | null; searches: number | null;
    /** Auxiliary users one seat brings, straight from the catalogue. */
    supportPerSeat?: number | null;
    features?: PlanFeatureItem[];
    /** Stated, never defaulted: a missing answer must not put a tier on sale. */
    sellable: boolean;
  }>,
  items: Array<{ plan_id: string; quantity: number }>,
): SeatUnlock[] => {
  const entry = plans[0]?.interactions ?? null;
  return plans.map((plan) => ({
    planId: plan.planId,
    title: plan.title,
    icon: plan.icon,
    seatsOnTeam: items.find((line) => line.plan_id === plan.planId)?.quantity ?? 0,
    supportPerSeat: plan.supportPerSeat ?? 0,
    interactions: plan.interactions,
    searches: plan.searches,
    timesEntry: entry && plan.interactions && plan.interactions > entry
      ? Math.round(plan.interactions / entry)
      : null,
    features: includedFeatures(plan.features),
    sellable: plan.sellable,
  }));
};

/**
 * The cards the ladder draws.
 *
 * A tier the catalogue no longer sells is dropped, because the ladder's whole
 * purpose is to offer one -- the screen used to list every plan the catalogue
 * returned and invite a change to a tier Payien would refuse to bill.
 *
 * Two exceptions keep an unsellable tier on screen, and both are the agency
 * reading its own subscription back: the tier its seats sit on, and the tier a
 * solo broker is on. Those cards describe what is already paid for; the CTA is
 * what gets withheld, not the card.
 *
 * @param unlocks - Every plan, cheapest first.
 * @param currentPlanId - The solo broker's tier, in the ladder's spelling.
 * @returns The subset worth drawing.
 */
export const onLadder = (
  unlocks: SeatUnlock[], currentPlanId: string | null = null,
): SeatUnlock[] => unlocks.filter(
  (unlock) => unlock.sellable
    || unlock.seatsOnTeam > 0
    || unlock.planId === currentPlanId,
);

/**
 * The plan above this one that can still be bought, or null when there is
 * none. An announced tier is skipped rather than offered: it is listed for
 * reading, and Payien would refuse the change.
 */
export const nextPlanUp = (
  unlocks: SeatUnlock[], planId: string | null,
): SeatUnlock | null => {
  const at = unlocks.findIndex((unlock) => unlock.planId === planId);
  if (at < 0) return null;
  return unlocks.slice(at + 1).find((unlock) => unlock.sellable) ?? null;
};

/** What the upgrade block needs to draw itself, or nothing to draw. */
export interface UpgradeOffer {
  current: SeatUnlock;
  target: SeatUnlock;
  seatFreeOnTarget: boolean;
}

/**
 * The offer to make, when there is one to make.
 *
 * Two conditions have to hold together and the block used to decide them
 * inline, inside the page's JSX, where nothing could reach them: somebody
 * has to be running out, and there has to be a plan above the one their
 * seat is on. A person on the top tier is under pressure with nothing to
 * sell them, and a seat on a plan the catalogue no longer lists has no
 * "current" to compare against -- both are silence, not an offer.
 *
 * `seatFreeOnTarget` changes the wording, not the decision: moving someone
 * into a seat the agency already pays for is a different sentence from
 * buying one.
 *
 * @param pressure - Who is running out, or null when nobody is.
 * @param unlocks - The plans, cheapest first.
 * @param seatsByPlan - Seat blocks the agency contracted, for the free count.
 * @returns The offer, or null when there is none to make.
 */
export const upgradeOffer = (
  pressure: UnderPressure | null,
  unlocks: SeatUnlock[],
  seatsByPlan?: Array<{ plan_id: string; free?: number }> | null,
): UpgradeOffer | null => {
  if (!pressure) return null;
  const planId = pressure.person.plan_id ?? null;
  const current = unlocks.find((unlock) => unlock.planId === planId);
  const target = nextPlanUp(unlocks, planId);
  if (!current || !target) return null;
  const block = (seatsByPlan || []).find((entry) => entry.plan_id === target.planId);
  return { current, target, seatFreeOnTarget: (block?.free ?? 0) > 0 };
};
